import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

const API_BASE = "/api/creator-radar";

const SITE_NAME = "Art For My Room";
const SITE_ORIGIN = "https://www.artformyroom.com";
const SITE_EMAIL = "info@artformyroom.com";

const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1513364776144-60967b0f800f?auto=format&fit=crop&w=1600&q=85";

const BRAND = {
  turquoise: "#00B4A6",
  turquoiseLight: "#5FF5EA",
  turquoiseDark: "#00796B",
  gold: "#D4AF37",
  goldLight: "#FFE08A",
  ink: "#071313",
  muted: "#637D7A",
};

function safeString(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
}

function safeArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean);

  if (typeof value === "string" && value.trim()) {
    return value
      .split(/\n|•|- /)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function compact(value, max = 170) {
  const clean = safeString(value).replace(/\s+/g, " ").trim();

  if (clean.length <= max) return clean;

  return `${clean.slice(0, max).trim()}…`;
}

function forceHttpsUrl(url) {
  const clean = safeString(url);

  if (!clean) return "";

  if (clean.startsWith("http://")) {
    return `https://${clean.slice("http://".length)}`;
  }

  return clean;
}

function isExternalUrl(url) {
  const clean = forceHttpsUrl(url);

  if (!clean) return false;

  try {
    const parsed = new URL(clean);
    return !parsed.hostname.includes("artformyroom.com");
  } catch {
    return false;
  }
}

function slugify(value) {
  return (
    safeString(value)
      .toLowerCase()
      .replace(/https?:\/\//g, "")
      .replace(/www\./g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "creator-radar"
  );
}

function formatDate(value, includeTime = false) {
  if (!value) return "Today";

  try {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleString("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      ...(includeTime
        ? {
            hour: "2-digit",
            minute: "2-digit",
          }
        : {}),
    });
  } catch {
    return String(value);
  }
}

function absoluteUrl(pathOrUrl) {
  const clean = forceHttpsUrl(pathOrUrl);

  if (!clean) return SITE_ORIGIN;
  if (clean.startsWith("https://")) return clean;
  if (clean.startsWith("/")) return `${SITE_ORIGIN}${clean}`;

  return `${SITE_ORIGIN}/${clean}`;
}

function getImage(item) {
  return forceHttpsUrl(
    item?.image_url ||
      item?.imageUrl ||
      item?.image ||
      item?.thumbnail ||
      item?.thumbnail_url ||
      item?.og_image ||
      item?.source_image_url ||
      DEFAULT_IMAGE
  );
}

function normaliseSource(raw, index = 0) {
  if (!raw) return null;

  if (typeof raw === "string") {
    return {
      id: `source-${index}`,
      title: raw,
      url: forceHttpsUrl(raw),
      source: "Original source",
      summary: "",
      published_at: "",
      image_url: DEFAULT_IMAGE,
    };
  }

  const url = forceHttpsUrl(
    raw.url ||
      raw.link ||
      raw.source_url ||
      raw.sourceUrl ||
      raw.sourceURL ||
      raw.original_url ||
      raw.originalUrl ||
      raw.article_url ||
      raw.articleUrl ||
      raw.href
  );

  const title =
    raw.title ||
    raw.headline ||
    raw.name ||
    raw.source_title ||
    raw.sourceTitle ||
    raw.original_title ||
    raw.originalTitle ||
    raw.article_title ||
    url ||
    `Source ${index + 1}`;

  return {
    id: raw.id || slugify(`${title}-${index}`),
    title,
    url,
    source:
      raw.source ||
      raw.publisher ||
      raw.source_name ||
      raw.sourceName ||
      raw.domain ||
      raw.source_domain ||
      raw.sourceDomain ||
      "Original source",
    summary:
      raw.summary ||
      raw.description ||
      raw.excerpt ||
      raw.snippet ||
      "Open the original source to read the full article.",
    published_at:
      raw.published_at ||
      raw.publishedAt ||
      raw.date ||
      raw.created_at ||
      "",
    image_url: getImage(raw),
  };
}

function extractSources(article, payload) {
  const rawSources = [
    ...safeArray(article?.sources),
    ...safeArray(article?.source_articles),
    ...safeArray(article?.sourceArticles),
    ...safeArray(article?.source_links),
    ...safeArray(article?.sourceLinks),
    ...safeArray(article?.source_links_list),
    ...safeArray(article?.sourceLinksList),
    ...safeArray(article?.links),
    ...safeArray(article?.references),
    ...safeArray(payload?.sources),
    ...safeArray(payload?.source_articles),
    ...safeArray(payload?.sourceArticles),
    ...safeArray(payload?.sourceLinks),
    ...safeArray(payload?.sections?.source_articles),
    ...safeArray(payload?.sections?.sourceArticles),
  ];

  const directUrl =
    article?.source_url ||
    article?.sourceUrl ||
    article?.sourceURL ||
    article?.original_url ||
    article?.originalUrl ||
    article?.article_url ||
    article?.articleUrl ||
    article?.external_url ||
    "";

  if (directUrl) {
    rawSources.unshift({
      title:
        article?.source_title ||
        article?.sourceTitle ||
        article?.original_title ||
        article?.originalTitle ||
        article?.title,
      url: directUrl,
      source:
        article?.source_name ||
        article?.sourceName ||
        article?.publisher ||
        article?.source ||
        "Original article",
      summary:
        article?.source_summary ||
        article?.sourceSummary ||
        article?.source_excerpt ||
        article?.summary,
      image_url: article?.source_image_url || article?.image_url,
      published_at: article?.source_published_at || article?.published_at,
    });
  }

  const seen = new Set();

  return rawSources
    .map(normaliseSource)
    .filter(Boolean)
    .filter((source) => {
      const key = source.url || source.title;

      if (!key || seen.has(key)) return false;

      seen.add(key);
      return true;
    });
}

function findArticleObject(payload) {
  return (
    payload?.daily_article ||
    payload?.dailyArticle ||
    payload?.article ||
    payload?.brief ||
    payload?.today ||
    payload?.featured_article ||
    payload?.featuredArticle ||
    payload?.featured_brief ||
    payload?.featuredBrief ||
    payload?.latest_article ||
    payload?.latestArticle ||
    payload?.current_article ||
    payload?.currentArticle ||
    payload?.sections?.daily_article ||
    payload?.sections?.dailyArticle ||
    payload?.sections?.featured_article ||
    payload?.sections?.featuredArticle ||
    payload?.sections?.featured_brief ||
    payload?.sections?.featuredBrief ||
    payload?.sections?.article ||
    payload?.data?.daily_article ||
    payload?.data?.dailyArticle ||
    payload?.data?.article ||
    payload?.data?.brief ||
    payload ||
    {}
  );
}

function hasGenericFallbackText(article) {
  const text = [
    article?.title,
    article?.summary,
    article?.description,
    safeArray(article?.key_points).join(" "),
    safeArray(article?.importantPoints).join(" "),
  ]
    .join(" ")
    .toLowerCase();

  return (
    text.includes("a daily artist-friendly summary of one useful art") ||
    text.includes("creator radar is designed as a daily artist digest") ||
    text.includes("a buyer needs more than a beautiful image")
  );
}

function normaliseArticle(payload) {
  const raw = findArticleObject(payload || {});
  const sources = extractSources(raw, payload || {});
  const primarySource = sources.find((source) => isExternalUrl(source.url)) || sources[0] || null;

  const sourceTitle =
    primarySource?.title ||
    raw?.original_title ||
    raw?.originalTitle ||
    raw?.source_title ||
    raw?.sourceTitle ||
    "";

  const title =
    raw?.title ||
    raw?.headline ||
    raw?.radar_title ||
    raw?.summary_title ||
    sourceTitle ||
    "Creator Radar article";

  const summary =
    raw?.summary ||
    raw?.artist_summary ||
    raw?.artistSummary ||
    raw?.seoDescription ||
    raw?.seo_description ||
    raw?.dek ||
    raw?.description ||
    raw?.excerpt ||
    "";

  const publishedAt =
    raw?.published_at ||
    raw?.publishedAt ||
    raw?.created_at ||
    raw?.date ||
    payload?.published_at ||
    payload?.date ||
    new Date().toISOString();

  const keyPoints = safeArray(
    raw?.importantPoints ||
      raw?.important_points ||
      raw?.key_points ||
      raw?.keyPoints ||
      raw?.takeaways ||
      raw?.artist_takeaways ||
      raw?.artistTakeaways
  );

  const artistActions = safeArray(
    raw?.artistActions ||
      raw?.artist_actions ||
      raw?.actions ||
      raw?.next_steps ||
      raw?.nextSteps ||
      raw?.practical_steps ||
      raw?.practicalSteps
  );

  const websiteAngle = safeArray(
    raw?.website_angle ||
      raw?.websiteAngle ||
      raw?.art_for_my_room_angle ||
      raw?.artForMyRoomAngle ||
      raw?.platform_angle ||
      raw?.business_angle
  );

  const paragraphs = safeArray(
    raw?.paragraphs ||
      raw?.short_read ||
      raw?.shortRead ||
      raw?.body_paragraphs ||
      raw?.body
  );

  const category =
    raw?.category ||
    raw?.topic ||
    raw?.section ||
    "Art news for artists";

  const slug =
    raw?.slug ||
    raw?.seo_slug ||
    slugify(`${publishedAt}-${title}`);

  const imageUrl = getImage(raw || primarySource);

  const isGenericFallback =
    Boolean(raw?.is_fallback || raw?.isFallback || payload?.is_fallback) ||
    hasGenericFallbackText(raw);

  const hasArticle =
    Boolean(summary || keyPoints.length || paragraphs.length) && !isGenericFallback;

  const hasRealSource = sources.some((source) => isExternalUrl(source.url));

  return {
    raw,
    id: raw?.id || slug,
    title,
    original_title: sourceTitle || title,
    summary,
    category,
    slug,
    published_at: publishedAt,
    updated_at: raw?.updated_at || raw?.modified_at || raw?.lastmod || publishedAt,
    read_time:
      raw?.readTime ||
      raw?.read_time ||
      raw?.reading_time ||
      `${raw?.read_time_min || 4} min read`,
    image_url: imageUrl,
    keywords: safeArray(raw?.keywords || raw?.tags || payload?.keywords),
    key_points: keyPoints,
    artist_actions: artistActions,
    website_angle: websiteAngle,
    paragraphs,
    sources,
    primary_source: primarySource,
    has_article: hasArticle,
    has_real_source: hasRealSource,
    is_generic_fallback: isGenericFallback,
    usefulness_score: raw?.usefulnessScore || raw?.usefulness_score || raw?.score || "",
    source_name: raw?.source || primarySource?.source || "",
  };
}

function normaliseArchiveItem(raw, index = 0) {
  const item = findArticleObject(raw || {});
  const article = normaliseArticle(item);

  return {
    id: raw?.id || article.slug || `archive-${index}`,
    title: raw?.title || article.title,
    summary:
      raw?.summary ||
      raw?.seoDescription ||
      raw?.meta_description ||
      article.summary ||
      "Saved Creator Radar summary.",
    slug: raw?.slug || article.slug,
    path: raw?.path || `/creator-radar/${raw?.slug || article.slug}`,
    published_at: raw?.published_at || raw?.date || article.published_at,
    image_url: raw?.image_url || article.image_url,
    source_url:
      raw?.source_url ||
      raw?.sourceUrl ||
      raw?.original_url ||
      raw?.url ||
      article.primary_source?.url ||
      "",
    has_real_article: article.has_article,
  };
}

function setMetaTag(name, content) {
  if (!content || typeof document === "undefined") return;

  let element = document.querySelector(`meta[name="${name}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function setPropertyMeta(property, content) {
  if (!content || typeof document === "undefined") return;

  let element = document.querySelector(`meta[property="${property}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function setCanonical(href) {
  if (!href || typeof document === "undefined") return;

  let element = document.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
}

function setJsonLd(id, payload) {
  if (!payload || typeof document === "undefined") return;

  let element = document.getElementById(id);

  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(payload);
}

async function fetchJsonCandidates(urls) {
  let lastError = "";

  for (const url of urls) {
    try {
      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
        },
      });

      const text = await response.text();
      let data = {};

      try {
        data = JSON.parse(text);
      } catch {
        data = { error: text };
      }

      if (!response.ok) {
        lastError = data?.detail || data?.error || `API ${response.status}`;
        continue;
      }

      return data;
    } catch (error) {
      lastError = error.message || "Could not fetch Creator Radar.";
    }
  }

  throw new Error(lastError || "No Creator Radar endpoint returned JSON.");
}

function LoadingState() {
  return (
    <div className="cr-loading">
      <div className="cr-loadingOrb" />
      <h2>Opening today’s Creator Radar</h2>
      <p>Looking for the latest artist article summary and source links.</p>
      <div className="cr-loaderLine" />
    </div>
  );
}

function KeyPointCard({ point, index }) {
  return (
    <article className="cr-point">
      <span>{String(index + 1).padStart(2, "0")}</span>
      <p>{point}</p>
    </article>
  );
}

function ActionCard({ action, index }) {
  return (
    <div className="cr-action">
      <span>{index + 1}</span>
      <p>{action}</p>
    </div>
  );
}

function SourceCard({ source }) {
  return (
    <article className="cr-sourceCard">
      <div
        className="cr-sourceImage"
        style={{
          backgroundImage: `url(${source.image_url || DEFAULT_IMAGE})`,
        }}
      >
        <span>{source.source || "Source"}</span>
      </div>

      <div className="cr-sourceBody">
        <div className="cr-miniMeta">
          <span>Original source</span>
          {source.published_at ? <span>{formatDate(source.published_at)}</span> : null}
        </div>

        <h3>{source.title}</h3>

        <p>{compact(source.summary, 180)}</p>

        {source.url ? (
          <a href={source.url} target="_blank" rel="noreferrer" className="cr-linkBtn">
            Read original article
          </a>
        ) : null}
      </div>
    </article>
  );
}

function ArchiveCard({ item }) {
  return (
    <article className="cr-archiveCard">
      <div
        className="cr-archiveImage"
        style={{
          backgroundImage: `url(${item.image_url || DEFAULT_IMAGE})`,
        }}
      />

      <div className="cr-archiveBody">
        <div className="cr-miniMeta">
          <span>{formatDate(item.published_at)}</span>
          <span>{item.has_real_article ? "Article" : "Saved"}</span>
        </div>

        <Link to={item.path || `/creator-radar/${item.slug}`}>
          {item.title}
        </Link>

        <p>{compact(item.summary, 110)}</p>
      </div>
    </article>
  );
}

function FriendlyEmptyState() {
  return (
    <section className="cr-friendlyEmpty">
      <div className="cr-emptyIcon">✦</div>
      <h2>Today’s Creator Radar is being prepared</h2>
      <p>
        The daily artist article will appear here once the latest source article,
        summary and artist actions are available.
      </p>

      <div className="cr-emptyButtons">
        <Link to="/creative-tech-opportunities">Explore artist opportunities</Link>
        <Link to="/artist-launch">Upload a painting</Link>
      </div>
    </section>
  );
}

export default function CreatorRadarPage() {
  const { slug } = useParams();

  const [payload, setPayload] = useState(null);
  const [archive, setArchive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [archiveSearch, setArchiveSearch] = useState("");

  const articleUrls = useMemo(() => {
    if (slug) {
      return [
        `${API_BASE}/${encodeURIComponent(slug)}/`,
        `${API_BASE}/article/${encodeURIComponent(slug)}/`,
      ];
    }

    const cacheBust = `t=${Date.now()}`;

    return [
      `${API_BASE}/today/?refresh=1&${cacheBust}`,
      `${API_BASE}/today/?force=1&${cacheBust}`,
      `${API_BASE}/today/?${cacheBust}`,
      `${API_BASE}/latest/?${cacheBust}`,
      `${API_BASE}/?${cacheBust}`,
      `/data/creator_radar/latest.json?${cacheBust}`,
      `/static/data/creator_radar/latest.json?${cacheBust}`,
    ];
  }, [slug]);

  useEffect(() => {
    let alive = true;

    async function loadArticle() {
      setLoading(true);
      setError("");

      try {
        const data = await fetchJsonCandidates(articleUrls);

        if (!alive) return;

        setPayload(data);
      } catch (err) {
        if (!alive) return;

        setPayload(null);
        setError(err.message || "Creator Radar article could not load.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadArticle();

    return () => {
      alive = false;
    };
  }, [articleUrls]);

  useEffect(() => {
    let alive = true;

    async function loadArchive() {
      setArchiveLoading(true);

      try {
        const data = await fetchJsonCandidates([
          `${API_BASE}/archive/?fresh=1&t=${Date.now()}`,
          `${API_BASE}/archive/?t=${Date.now()}`,
          `/data/creator_radar/manifest.json?t=${Date.now()}`,
          `/static/data/creator_radar/manifest.json?t=${Date.now()}`,
        ]);

        if (!alive) return;

        const rawItems = [
          ...safeArray(data.articles),
          ...safeArray(data.results),
          ...safeArray(data.archive),
          ...safeArray(data.items),
          ...safeArray(data?.sections?.archive_cards),
        ];

        setArchive(rawItems.map(normaliseArchiveItem));
      } catch {
        if (alive) setArchive([]);
      } finally {
        if (alive) setArchiveLoading(false);
      }
    }

    loadArchive();

    return () => {
      alive = false;
    };
  }, []);

  const article = useMemo(() => normaliseArticle(payload || {}), [payload]);

  const canonicalPath = article.has_article
    ? `/creator-radar/${article.slug}`
    : "/creator-radar";

  const canonicalUrl = absoluteUrl(canonicalPath);

  const archiveCards = useMemo(() => {
    const q = archiveSearch.trim().toLowerCase();

    if (!q) return archive;

    return archive.filter((item) =>
      [item.title, item.summary, item.slug, item.source_url]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [archive, archiveSearch]);

  const keyPoints =
    article.key_points.length > 0
      ? article.key_points
      : [
          article.summary,
          "Artists can use this story as a signal for how buyers, platforms, galleries or technology are changing.",
          "The useful question is not only what happened, but what artists can improve today.",
        ].filter(Boolean);

  const artistActions =
    article.artist_actions.length > 0
      ? article.artist_actions
      : [
          "Review one painting listing and make the room, size, colour mood and buyer value clearer.",
          "Use the article as a social post prompt and add your own artist perspective.",
          "Upload or update one artwork so buyers can discover it through room matching.",
        ];

  const websiteAngle =
    article.website_angle.length > 0
      ? article.website_angle
      : [
          "Better online presentation helps buyers imagine original paintings in real rooms.",
          "Technology can support artists when it makes discovery easier, not colder.",
          "A daily art signal gives painters one practical reason to return and improve their visibility.",
        ];

  const source = article.primary_source;

  const shareText = [
    article.title,
    "",
    article.summary,
    "",
    source?.url ? `Original article: ${source.url}` : "",
    `Creator Radar: ${canonicalUrl}`,
    "",
    "#ArtForMyRoom #CreatorRadar #Artists",
  ]
    .filter(Boolean)
    .join("\n");

  useEffect(() => {
    const pageTitle = article.has_article
      ? `${article.title} | Creator Radar | Art For My Room`
      : "Creator Radar | Daily Artist News | Art For My Room";

    const description = article.has_article
      ? compact(article.summary, 158)
      : "Daily artist news summaries for painters, creative technology, art markets and online painting discovery.";

    document.title = pageTitle;

    setCanonical(canonicalUrl);
    setMetaTag("description", description);
    setMetaTag(
      "keywords",
      [
        "artist news",
        "art market news",
        "AI for artists",
        "creative technology",
        "sell paintings online",
        "painting marketplace",
        "Art For My Room",
        ...article.keywords,
      ].join(", ")
    );
    setMetaTag("robots", "index, follow, max-image-preview:large, max-snippet:-1");
    setMetaTag("theme-color", BRAND.turquoise);

    setPropertyMeta("og:site_name", SITE_NAME);
    setPropertyMeta("og:title", pageTitle);
    setPropertyMeta("og:description", description);
    setPropertyMeta("og:type", article.has_article ? "article" : "website");
    setPropertyMeta("og:url", canonicalUrl);
    setPropertyMeta("og:image", absoluteUrl(article.image_url || DEFAULT_IMAGE));

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", pageTitle);
    setMetaTag("twitter:description", description);
    setMetaTag("twitter:image", absoluteUrl(article.image_url || DEFAULT_IMAGE));

    if (article.has_article) {
      setJsonLd("creator-radar-jsonld", {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description,
        image: absoluteUrl(article.image_url || DEFAULT_IMAGE),
        datePublished: article.published_at,
        dateModified: article.updated_at,
        mainEntityOfPage: canonicalUrl,
        author: {
          "@type": "Organization",
          name: SITE_NAME,
        },
        publisher: {
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_ORIGIN,
        },
        citation: article.sources.map((item) => item.url).filter(Boolean),
      });
    }
  }, [article, canonicalUrl]);

  async function copySummary() {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setError("Could not copy the summary.");
    }
  }

  async function shareArticle() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.summary,
          url: canonicalUrl,
        });
      } catch {
        // user cancelled share
      }
    } else {
      copySummary();
    }
  }

  function shareLinkedIn() {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        canonicalUrl
      )}`,
      "_blank",
      "noopener,noreferrer,width=900,height=720"
    );
  }

  function shareFacebook() {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonicalUrl)}`,
      "_blank",
      "noopener,noreferrer,width=900,height=720"
    );
  }

  function shareX() {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        article.title
      )}&url=${encodeURIComponent(canonicalUrl)}`,
      "_blank",
      "noopener,noreferrer,width=900,height=720"
    );
  }

  if (loading) {
    return (
      <main className="cr-page">
        <Styles />
        <div className="cr-shell">
          <LoadingState />
        </div>
      </main>
    );
  }

  return (
    <main className="cr-page">
      <Styles />

      <div className="cr-particles">
        <i />
        <i />
        <i />
        <i />
        <i />
      </div>

      <div className="cr-shell">
        <section className="cr-hero">
          <div className="cr-heroPattern" />

          <div className="cr-heroText">
            <div className="cr-eyebrow">
              ✦ Creator Radar · daily artist intelligence
            </div>

            <h1>
              {article.has_article
                ? article.title
                : "Creator Radar is preparing today’s artist signal"}
            </h1>

            <p>
              {article.has_article
                ? article.summary
                : "A concise daily article summary for painters, art buyers and creative builders will appear here when today’s radar is available."}
            </p>

            <div className="cr-pills">
              <span>{formatDate(article.published_at)}</span>
              <span>{article.read_time}</span>
              <span>{article.category}</span>
              {article.source_name ? <span>{article.source_name}</span> : null}
              {article.usefulness_score ? (
                <span>{article.usefulness_score}% useful</span>
              ) : null}
            </div>

            <div className="cr-heroActions">
              {source?.url ? (
                <a href={source.url} target="_blank" rel="noreferrer" className="cr-primaryBtn">
                  Read original article
                </a>
              ) : null}

              {article.has_article ? (
                <>
                  <button type="button" onClick={shareArticle} className="cr-secondaryBtn">
                    Share
                  </button>

                  <button type="button" onClick={copySummary} className="cr-secondaryBtn">
                    {copied ? "Copied" : "Copy summary"}
                  </button>
                </>
              ) : (
                <Link to="/creative-tech-opportunities" className="cr-primaryBtn">
                  Explore artist opportunities
                </Link>
              )}
            </div>
          </div>

          <div className="cr-heroCard">
            <div
              className="cr-heroImage"
              style={{ backgroundImage: `url(${article.image_url || DEFAULT_IMAGE})` }}
            >
              <span>{article.has_article ? "Today’s signal" : "Artist radar"}</span>
            </div>

            <div className="cr-statGrid">
              <div>
                <strong>{article.sources.length}</strong>
                <span>source links</span>
              </div>
              <div>
                <strong>{archive.length}</strong>
                <span>archive items</span>
              </div>
              <div>
                <strong>{keyPoints.length}</strong>
                <span>key points</span>
              </div>
              <div>
                <strong>{article.has_article ? "Live" : "Ready"}</strong>
                <span>status</span>
              </div>
            </div>
          </div>
        </section>

        {error ? <div className="cr-alert cr-alertError">{error}</div> : null}

        {!article.has_article ? (
          <FriendlyEmptyState />
        ) : (
          <>
            <section className="cr-gridMain">
              <article className="cr-panel cr-articlePanel">
                <div className="cr-sectionHead">
                  <span>Artist summary</span>
                  <h2>What artists need to know today</h2>
                  <p>
                    A concise summary of one real article, rewritten for painters,
                    creative sellers and artists using technology to become easier to discover.
                  </p>
                </div>

                <div className="cr-pointsGrid">
                  {keyPoints.slice(0, 6).map((point, index) => (
                    <KeyPointCard key={`${point}-${index}`} point={point} index={index} />
                  ))}
                </div>

                <div className="cr-shortRead">
                  <h3>The short read</h3>

                  {(article.paragraphs.length ? article.paragraphs : [article.summary]).map(
                    (paragraph, index) => (
                      <p key={`${paragraph}-${index}`}>{paragraph}</p>
                    )
                  )}
                </div>

                <div className="cr-takeawayBox">
                  <h3>How this helps Art For My Room artists</h3>

                  <div>
                    {websiteAngle.slice(0, 6).map((item, index) => (
                      <p key={`${item}-${index}`}>✓ {item}</p>
                    ))}
                  </div>
                </div>
              </article>

              <aside className="cr-side">
                <div className="cr-darkPanel">
                  <span>Studio action</span>
                  <h2>Turn today’s article into one move</h2>
                  <p>
                    News is useful when it changes a listing, a room preview,
                    a buyer message, a photo, or a painting upload.
                  </p>

                  <div className="cr-actionsList">
                    {artistActions.slice(0, 5).map((action, index) => (
                      <ActionCard key={`${action}-${index}`} action={action} index={index} />
                    ))}
                  </div>
                </div>

                <div className="cr-panel">
                  <h3>Share this radar</h3>
                  <p className="cr-muted">
                    Useful for painters? Send the signal into the little artist constellation.
                  </p>

                  <div className="cr-shareButtons">
                    <button type="button" onClick={shareLinkedIn}>
                      LinkedIn
                    </button>
                    <button type="button" onClick={shareFacebook}>
                      Facebook
                    </button>
                    <button type="button" onClick={shareX}>
                      X
                    </button>
                  </div>
                </div>
              </aside>
            </section>

            <section className="cr-section">
              <div className="cr-sectionHead cr-sectionCenter">
                <span>Original article</span>
                <h2>The real source behind today’s radar</h2>
                <p>
                  Creator Radar is a summary, not a replacement. The original source
                  stays visible so artists can read the full context.
                </p>
              </div>

              <div className="cr-sourceGrid">
                {article.sources.length ? (
                  article.sources.map((item, index) => (
                    <SourceCard key={`${item.url}-${index}`} source={item} />
                  ))
                ) : (
                  <div className="cr-alert">
                    The article summary is available, but no source card was returned.
                  </div>
                )}
              </div>
            </section>
          </>
        )}

        <section className="cr-section">
          <div className="cr-sectionTop">
            <div className="cr-sectionHead">
              <span>Daily archive</span>
              <h2>Saved summaries</h2>
              <p>
                Every generated article becomes a small SEO doorway and a useful archive
                for painters returning to the platform.
              </p>
            </div>

            <input
              value={archiveSearch}
              onChange={(event) => setArchiveSearch(event.target.value)}
              placeholder="Search archive..."
              className="cr-search"
            />
          </div>

          {archiveLoading ? (
            <div className="cr-alert">Loading archive...</div>
          ) : archiveCards.length ? (
            <div className="cr-archiveGrid">
              {archiveCards.slice(0, 12).map((item) => (
                <ArchiveCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="cr-alert">
              No archive returned yet. Once the backend saves daily articles, they will
              appear here.
            </div>
          )}
        </section>

        <section className="cr-cta">
          <div>
            <span>Turn reading into showing</span>
            <h2>Read the signal, then upload the painting.</h2>
            <p>
              Art For My Room helps painters list original works for room-aware discovery,
              while buyers upload real interiors to find paintings that belong there.
            </p>
          </div>

          <div className="cr-ctaButtons">
            <Link to="/artist-launch">Upload painting</Link>
            <Link to="/artist-room-matcher">Match a room</Link>
            <Link to="/creative-tech-opportunities">Opportunities</Link>
          </div>
        </section>
      </div>

      <footer className="cr-footer">
        <div className="cr-footerInner">
          <div>
            <h3>Creator Radar</h3>
            <p>
              One real article, translated into useful artist action. Built by Art For My Room.
            </p>
          </div>

          <div>
            <h4>Explore</h4>
            <a href="/">Home</a>
            <a href="/artist-room-matcher">Find Painting</a>
            <a href="/artist-launch">Upload Painting</a>
            <a href="/creative-tech-opportunities">Artist Opportunities</a>
          </div>

          <div>
            <h4>Creator Radar includes</h4>
            <span>Daily article summary</span>
            <span>Key points for artists</span>
            <span>Original source link</span>
            <span>Archive and sharing</span>
          </div>

          <div>
            <h4>Contact</h4>
            <a href={`mailto:${SITE_EMAIL}`}>{SITE_EMAIL}</a>
            <span>Art news for real rooms and real paintings.</span>
          </div>
        </div>

        <div className="cr-footerBottom">
          <span>© {new Date().getFullYear()} Art For My Room</span>
          <span>Daily signals for painters, buyers and creative builders.</span>
        </div>
      </footer>
    </main>
  );
}

function Styles() {
  return (
    <style>{`
      .cr-page {
        min-height: 100vh;
        color: ${BRAND.ink};
        background:
          radial-gradient(circle at 10% 8%, rgba(0,180,166,.22), transparent 30%),
          radial-gradient(circle at 88% 10%, rgba(212,175,55,.18), transparent 28%),
          linear-gradient(180deg, #f8fffd 0%, #ecfffc 45%, #fff9ec 100%);
        font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        position: relative;
        overflow-x: hidden;
      }

      .cr-page::before {
        content: "";
        position: fixed;
        inset: 0;
        pointer-events: none;
        background-image:
          linear-gradient(rgba(0,180,166,.12) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,180,166,.12) 1px, transparent 1px);
        background-size: 38px 38px;
        opacity: .28;
      }

      .cr-particles {
        position: fixed;
        inset: 0;
        pointer-events: none;
        overflow: hidden;
        z-index: 1;
      }

      .cr-particles i {
        position: absolute;
        top: -30px;
        width: 8px;
        height: 8px;
        border-radius: 999px;
        background: ${BRAND.turquoiseLight};
        animation: crFall 14s linear infinite;
        opacity: .5;
      }

      .cr-particles i:nth-child(1) { left: 8%; animation-delay: 0s; }
      .cr-particles i:nth-child(2) { left: 28%; animation-delay: 2s; background: ${BRAND.goldLight}; }
      .cr-particles i:nth-child(3) { left: 50%; animation-delay: 5s; }
      .cr-particles i:nth-child(4) { left: 72%; animation-delay: 1s; background: ${BRAND.gold}; }
      .cr-particles i:nth-child(5) { left: 90%; animation-delay: 4s; }

      .cr-shell {
        position: relative;
        z-index: 2;
        width: min(1240px, calc(100% - 28px));
        margin: 0 auto;
        padding: 28px 0 84px;
      }

      .cr-hero {
        position: relative;
        overflow: hidden;
        border-radius: 42px;
        color: #fff;
        min-height: 560px;
        padding: clamp(24px, 5vw, 62px);
        display: grid;
        grid-template-columns: minmax(0, 1.35fr) minmax(330px, .65fr);
        gap: 34px;
        align-items: center;
        background:
          radial-gradient(circle at 15% 18%, rgba(95,245,234,.30), transparent 30%),
          radial-gradient(circle at 86% 14%, rgba(255,224,138,.24), transparent 28%),
          linear-gradient(135deg, #031817 0%, #07312f 52%, #2f2508 100%);
        box-shadow: 0 42px 110px rgba(0,80,74,.26);
        border: 1px solid rgba(255,255,255,.16);
      }

      .cr-heroPattern {
        position: absolute;
        inset: 0;
        opacity: .13;
        background-image:
          linear-gradient(rgba(255,255,255,.22) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,.22) 1px, transparent 1px);
        background-size: 34px 34px;
      }

      .cr-heroText,
      .cr-heroCard {
        position: relative;
        z-index: 2;
      }

      .cr-eyebrow,
      .cr-sectionHead span,
      .cr-darkPanel span,
      .cr-cta span {
        display: inline-flex;
        color: ${BRAND.goldLight};
        font-weight: 950;
        text-transform: uppercase;
        letter-spacing: .14em;
        font-size: 11px;
        margin-bottom: 12px;
      }

      .cr-hero h1 {
        margin: 0;
        font-size: clamp(2.35rem, 6vw, 5.1rem);
        letter-spacing: -.075em;
        line-height: .94;
        max-width: 950px;
      }

      .cr-hero p {
        max-width: 820px;
        color: rgba(255,255,255,.78);
        line-height: 1.85;
        font-size: 1.08rem;
        margin: 22px 0 0;
      }

      .cr-pills,
      .cr-heroActions,
      .cr-shareButtons,
      .cr-ctaButtons {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
      }

      .cr-pills {
        margin-top: 24px;
      }

      .cr-pills span,
      .cr-miniMeta span {
        display: inline-flex;
        padding: 8px 11px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 850;
      }

      .cr-pills span {
        color: #fff;
        background: rgba(255,255,255,.11);
        border: 1px solid rgba(255,255,255,.14);
        backdrop-filter: blur(10px);
      }

      .cr-heroActions {
        margin-top: 28px;
      }

      .cr-primaryBtn,
      .cr-secondaryBtn,
      .cr-linkBtn,
      .cr-ctaButtons a,
      .cr-emptyButtons a {
        border: none;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;
        font: inherit;
        font-weight: 950;
        border-radius: 999px;
        padding: 13px 18px;
      }

      .cr-primaryBtn,
      .cr-linkBtn,
      .cr-ctaButtons a:first-child,
      .cr-emptyButtons a:first-child {
        color: #fff;
        background: linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.gold});
        box-shadow: 0 16px 36px rgba(0,180,166,.24);
      }

      .cr-secondaryBtn,
      .cr-ctaButtons a,
      .cr-emptyButtons a {
        color: #fff;
        background: rgba(255,255,255,.12);
        border: 1px solid rgba(255,255,255,.18);
      }

      .cr-heroCard {
        padding: 18px;
        border-radius: 34px;
        background: rgba(255,255,255,.10);
        border: 1px solid rgba(255,255,255,.14);
        box-shadow: 0 28px 70px rgba(0,0,0,.22);
        backdrop-filter: blur(16px);
      }

      .cr-heroImage {
        height: 330px;
        border-radius: 26px;
        background-size: cover;
        background-position: center;
        position: relative;
        overflow: hidden;
      }

      .cr-heroImage::after,
      .cr-sourceImage::after,
      .cr-archiveImage::after {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, transparent 35%, rgba(0,0,0,.55));
      }

      .cr-heroImage span,
      .cr-sourceImage span {
        position: absolute;
        left: 14px;
        bottom: 14px;
        z-index: 2;
        padding: 8px 11px;
        border-radius: 999px;
        color: #fff;
        background: rgba(0,0,0,.48);
        font-size: 12px;
        font-weight: 950;
      }

      .cr-statGrid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 10px;
        margin-top: 14px;
      }

      .cr-statGrid div {
        padding: 15px;
        border-radius: 20px;
        background: rgba(255,255,255,.10);
        border: 1px solid rgba(255,255,255,.12);
      }

      .cr-statGrid strong {
        display: block;
        color: ${BRAND.goldLight};
        font-size: 1.2rem;
      }

      .cr-statGrid span {
        color: rgba(255,255,255,.67);
        font-size: 12px;
      }

      .cr-alert,
      .cr-friendlyEmpty {
        margin-top: 24px;
        padding: 28px;
        border-radius: 30px;
        background: rgba(255,255,255,.88);
        border: 1px solid rgba(0,180,166,.12);
        box-shadow: 0 18px 42px rgba(0,80,74,.09);
        line-height: 1.7;
      }

      .cr-friendlyEmpty {
        text-align: center;
        padding: clamp(34px, 5vw, 58px);
      }

      .cr-emptyIcon {
        width: 62px;
        height: 62px;
        margin: 0 auto 16px;
        display: grid;
        place-items: center;
        border-radius: 999px;
        color: #fff;
        font-size: 1.4rem;
        background: linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.gold});
        box-shadow: 0 18px 42px rgba(0,180,166,.2);
      }

      .cr-friendlyEmpty h2 {
        margin: 0;
        color: #064e46;
        font-size: clamp(2rem, 4vw, 3rem);
        letter-spacing: -.055em;
      }

      .cr-friendlyEmpty p {
        color: ${BRAND.muted};
        max-width: 720px;
        margin: 16px auto 0;
        line-height: 1.8;
      }

      .cr-emptyButtons {
        margin-top: 24px;
        display: flex;
        justify-content: center;
        gap: 12px;
        flex-wrap: wrap;
      }

      .cr-alertError {
        background: rgba(255,240,240,.92);
        color: #8a2f2f;
      }

      .cr-gridMain {
        margin-top: 34px;
        display: grid;
        grid-template-columns: minmax(0, 1fr) 390px;
        gap: 28px;
        align-items: start;
      }

      .cr-panel,
      .cr-darkPanel {
        border-radius: 32px;
        padding: clamp(22px, 3vw, 36px);
        box-shadow: 0 28px 80px rgba(0,80,74,.12);
      }

      .cr-panel {
        background: rgba(255,255,255,.88);
        border: 1px solid rgba(255,255,255,.74);
        backdrop-filter: blur(18px);
      }

      .cr-darkPanel {
        color: #fff;
        background:
          radial-gradient(circle at 12% 14%, rgba(0,180,166,.20), transparent 34%),
          radial-gradient(circle at 92% 18%, rgba(212,175,55,.18), transparent 32%),
          linear-gradient(135deg, #041918 0%, #061f1e 55%, #2b2107 100%);
        border: 1px solid rgba(255,255,255,.14);
        position: sticky;
        top: 100px;
      }

      .cr-sectionHead h2,
      .cr-shortRead h3,
      .cr-takeawayBox h3,
      .cr-darkPanel h2,
      .cr-panel h3,
      .cr-cta h2 {
        margin: 0;
        color: #064e46;
        letter-spacing: -.055em;
        line-height: 1.04;
      }

      .cr-sectionHead h2 {
        font-size: clamp(2rem, 4vw, 3.1rem);
      }

      .cr-sectionHead p,
      .cr-muted {
        color: ${BRAND.muted};
        line-height: 1.8;
      }

      .cr-darkPanel h2,
      .cr-darkPanel h3 {
        color: #fff;
        font-size: clamp(1.8rem, 3vw, 2.45rem);
      }

      .cr-darkPanel p {
        color: rgba(255,255,255,.72);
        line-height: 1.8;
      }

      .cr-pointsGrid {
        margin-top: 24px;
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
      }

      .cr-point {
        padding: 20px;
        border-radius: 24px;
        background: rgba(0,180,166,.07);
        border: 1px solid rgba(0,180,166,.14);
      }

      .cr-point:nth-child(even) {
        background: rgba(212,175,55,.08);
        border-color: rgba(212,175,55,.16);
      }

      .cr-point span {
        display: block;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        color: ${BRAND.turquoiseDark};
        font-weight: 950;
        margin-bottom: 8px;
      }

      .cr-point p,
      .cr-shortRead p,
      .cr-takeawayBox p {
        margin: 0;
        color: rgba(7,19,19,.74);
        line-height: 1.82;
      }

      .cr-shortRead {
        margin-top: 32px;
        padding-top: 28px;
        border-top: 1px solid #d8efeb;
      }

      .cr-shortRead h3,
      .cr-takeawayBox h3 {
        font-size: clamp(1.55rem, 3vw, 2.1rem);
        margin-bottom: 14px;
      }

      .cr-shortRead p + p {
        margin-top: 14px;
      }

      .cr-takeawayBox {
        margin-top: 26px;
        padding: 22px;
        border-radius: 24px;
        background: linear-gradient(135deg, rgba(0,180,166,.08), rgba(212,175,55,.10));
        border: 1px solid rgba(0,180,166,.16);
      }

      .cr-actionsList {
        display: grid;
        gap: 14px;
        margin-top: 20px;
      }

      .cr-action {
        display: flex;
        gap: 12px;
        align-items: flex-start;
      }

      .cr-action span {
        width: 34px;
        height: 34px;
        border-radius: 999px;
        display: grid;
        place-items: center;
        flex-shrink: 0;
        color: #fff;
        background: linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.gold});
        font-weight: 950;
      }

      .cr-action p {
        margin: 0;
      }

      .cr-side {
        display: grid;
        gap: 22px;
      }

      .cr-shareButtons button {
        border: 1px solid rgba(0,180,166,.18);
        background: rgba(255,255,255,.88);
        color: ${BRAND.turquoiseDark};
        border-radius: 999px;
        padding: 11px 14px;
        cursor: pointer;
        font: inherit;
        font-weight: 900;
      }

      .cr-section {
        margin-top: 50px;
      }

      .cr-sectionCenter {
        text-align: center;
        max-width: 820px;
        margin: 0 auto 24px;
      }

      .cr-sourceGrid,
      .cr-archiveGrid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 18px;
      }

      .cr-sourceCard,
      .cr-archiveCard {
        overflow: hidden;
        border-radius: 28px;
        background: rgba(255,255,255,.90);
        border: 1px solid rgba(0,180,166,.12);
        box-shadow: 0 18px 42px rgba(0,80,74,.10);
      }

      .cr-sourceImage {
        height: 180px;
        background-size: cover;
        background-position: center;
        position: relative;
      }

      .cr-sourceBody,
      .cr-archiveBody {
        padding: 18px;
      }

      .cr-miniMeta {
        display: flex;
        gap: 7px;
        flex-wrap: wrap;
        margin-bottom: 10px;
      }

      .cr-miniMeta span {
        color: ${BRAND.turquoiseDark};
        background: rgba(0,180,166,.09);
      }

      .cr-sourceBody h3 {
        margin: 0;
        color: ${BRAND.ink};
        line-height: 1.14;
        letter-spacing: -.035em;
      }

      .cr-sourceBody p,
      .cr-archiveBody p {
        color: ${BRAND.muted};
        line-height: 1.65;
      }

      .cr-sectionTop {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 16px;
        align-items: end;
        margin-bottom: 18px;
      }

      .cr-search {
        border: 1px solid rgba(0,180,166,.15);
        background: rgba(255,255,255,.88);
        color: ${BRAND.ink};
        border-radius: 999px;
        padding: 13px 16px;
        min-width: 280px;
        font: inherit;
      }

      .cr-archiveImage {
        height: 120px;
        background-size: cover;
        background-position: center;
        position: relative;
      }

      .cr-archiveBody a {
        display: block;
        color: ${BRAND.ink};
        font-weight: 950;
        line-height: 1.2;
        text-decoration: none;
      }

      .cr-archiveBody a:hover {
        color: ${BRAND.turquoiseDark};
      }

      .cr-cta {
        margin-top: 50px;
        padding: clamp(24px, 4vw, 40px);
        border-radius: 34px;
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 24px;
        align-items: center;
        background: rgba(255,255,255,.88);
        border: 1px solid rgba(255,255,255,.74);
        box-shadow: 0 28px 80px rgba(0,80,74,.12);
      }

      .cr-cta h2 {
        font-size: clamp(2rem, 4vw, 2.8rem);
      }

      .cr-cta p {
        color: ${BRAND.muted};
        line-height: 1.8;
        max-width: 760px;
      }

      .cr-footer {
        position: relative;
        z-index: 2;
        color: #fff;
        background:
          radial-gradient(circle at 18% 20%, rgba(0,180,166,.22), transparent 30%),
          radial-gradient(circle at 82% 20%, rgba(212,175,55,.18), transparent 30%),
          linear-gradient(135deg, #061414 0%, #111b1a 58%, #171103 100%);
        border-radius: 34px 34px 0 0;
      }

      .cr-footerInner {
        width: min(1240px, calc(100% - 28px));
        margin: 0 auto;
        padding: 44px 0 28px;
        display: grid;
        grid-template-columns: 1.2fr repeat(3, 1fr);
        gap: 26px;
      }

      .cr-footer h3,
      .cr-footer h4 {
        margin: 0 0 12px;
      }

      .cr-footer p,
      .cr-footer a,
      .cr-footer span {
        display: block;
        color: rgba(255,255,255,.70);
        line-height: 1.65;
        text-decoration: none;
        margin: 7px 0;
      }

      .cr-footer a:hover {
        color: ${BRAND.goldLight};
      }

      .cr-footerBottom {
        width: min(1240px, calc(100% - 28px));
        margin: 0 auto;
        padding: 18px 0 24px;
        display: flex;
        justify-content: space-between;
        gap: 14px;
        border-top: 1px solid rgba(255,255,255,.14);
        color: rgba(255,255,255,.58);
        font-size: 14px;
      }

      .cr-loading {
        margin-top: 30px;
        padding: 44px;
        text-align: center;
        border-radius: 32px;
        background: rgba(255,255,255,.88);
        border: 1px solid rgba(0,180,166,.12);
        box-shadow: 0 28px 80px rgba(0,80,74,.12);
      }

      .cr-loadingOrb {
        width: 74px;
        height: 74px;
        margin: 0 auto 18px;
        border-radius: 999px;
        background: radial-gradient(circle at 35% 32%, #fff7b0, ${BRAND.turquoise} 68%, ${BRAND.gold});
        animation: crPulse 1.8s ease-in-out infinite;
      }

      .cr-loading h2 {
        color: #064e46;
        letter-spacing: -.04em;
      }

      .cr-loading p {
        color: ${BRAND.muted};
      }

      .cr-loaderLine {
        height: 10px;
        border-radius: 999px;
        margin-top: 22px;
        overflow: hidden;
        background: rgba(0,180,166,.12);
      }

      .cr-loaderLine::after {
        content: "";
        display: block;
        height: 100%;
        width: 46%;
        background: linear-gradient(90deg, ${BRAND.turquoise}, ${BRAND.gold});
        animation: crSlide 1.4s ease-in-out infinite;
      }

      @keyframes crFall {
        0% { transform: translateY(-20px); opacity: 0; }
        12% { opacity: .65; }
        100% { transform: translateY(120vh); opacity: 0; }
      }

      @keyframes crPulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.06); }
      }

      @keyframes crSlide {
        0% { transform: translateX(-110%); }
        100% { transform: translateX(240%); }
      }

      @media (max-width: 1040px) {
        .cr-hero,
        .cr-gridMain,
        .cr-cta,
        .cr-sectionTop {
          grid-template-columns: 1fr;
        }

        .cr-sourceGrid,
        .cr-archiveGrid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .cr-darkPanel {
          position: static;
        }

        .cr-footerInner {
          grid-template-columns: 1fr 1fr;
        }
      }

      @media (max-width: 720px) {
        .cr-shell {
          width: min(100% - 22px, 1240px);
          padding-top: 14px;
        }

        .cr-hero {
          border-radius: 28px;
          padding: 22px;
        }

        .cr-hero h1 {
          font-size: clamp(2.15rem, 12vw, 3.35rem);
        }

        .cr-heroImage {
          height: 255px;
        }

        .cr-pointsGrid,
        .cr-sourceGrid,
        .cr-archiveGrid,
        .cr-footerInner {
          grid-template-columns: 1fr;
        }

        .cr-search {
          min-width: 0;
          width: 100%;
        }

        .cr-footerBottom {
          flex-direction: column;
        }
      }
    `}</style>
  );
}
